import { AnimatePresence, motion } from "motion/react";
import styled from "@emotion/styled";
import {
  MDSBubble,
  MDSBubbleAlignment,
  MDSBubbleVariant,
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSLink,
} from "@/mds";
import type {
  AudioCaptureTranscriptEntry,
  AudioCaptureTranscriptState,
} from "../audio-capture-actions.types";
import { MutableRefObject, type MouseEvent, useEffect, useState } from "react";
import { isMac } from "@/domains/platform/isMac";
import { isWin } from "@/domains/platform/isWin";
import {
  AudioCaptureTranscriptViewMode,
  getAudioCaptureTranscriptViewState,
} from "./audio-capture-transcript.model";
import { AudioCapturePhase } from "@/store/audio/pipeline/audio-capture.types";
import { TranscriptSpeakerBadge } from "@/components/note/transcript-speaker-badge";

interface AudioCaptureTranscriptProps {
  transcript: AudioCaptureTranscriptState;
  isAdmin: boolean;
  isTextTranscript: boolean;
  onToggleAdminMode: () => void;
  transcriptContainerRef: MutableRefObject<HTMLDivElement | null>;
  onTranscriptScroll: () => void;
  isMicrophoneDisconnected: boolean;
  isAudioSilent: boolean;
  phase: AudioCapturePhase;
  shouldShowDesktopDownloadNotice: boolean;
  onDownloadClick: () => void;
  onOpenMicrophoneSystemPermissions: () => void;
  audioRecordingFailureMessage?: string | null;
}

export function AudioCaptureTranscript({
  transcript,
  isAdmin,
  isTextTranscript,
  onToggleAdminMode,
  transcriptContainerRef,
  onTranscriptScroll,
  isMicrophoneDisconnected,
  isAudioSilent,
  phase,
  shouldShowDesktopDownloadNotice,
  onDownloadClick,
  onOpenMicrophoneSystemPermissions,
  audioRecordingFailureMessage,
}: AudioCaptureTranscriptProps) {
  const [showMicrophoneAccessNotice, setShowMicrophoneAccessNotice] = useState(false);
  const viewState = getAudioCaptureTranscriptViewState({
    transcript,
    phase,
    isAudioSilent,
    audioRecordingFailureMessage,
  });

  useEffect(() => {
    if (phase !== AudioCapturePhase.Initializing) {
      setShowMicrophoneAccessNotice(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowMicrophoneAccessNotice(true);
    }, 10_000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [phase]);

  const handleTranscriptClick = (event: MouseEvent<HTMLDivElement>) => {
    if (isAdmin && event.altKey) {
      onToggleAdminMode();
    }
  };
  const transcriptBubbleGroups = groupAudioCaptureTranscriptEntries(transcript.entries);

  if (viewState.mode === AudioCaptureTranscriptViewMode.Transcript) {
    return (
      <StyledTranscriptContainer ref={transcriptContainerRef} onScroll={onTranscriptScroll}>
        <StyledTranscriptContent onClick={handleTranscriptClick}>
          {isTextTranscript && <span>{transcript.fullText}</span>}

          {!isTextTranscript &&
            transcriptBubbleGroups.map(group => (
              <AudioCaptureTranscriptBubbleGroup key={group.id} group={group} />
            ))}
          {transcript.handoffStatusLabel && (
            <TranscriptStatus>
              <MDSIcon size={12} kind={MDSIconKind.Microphone} color={MDSIconColor.Secondary} />
              <span>{transcript.handoffStatusLabel}</span>
            </TranscriptStatus>
          )}
          {viewState.showDisconnected && (
            <TranscriptStatus>
              <MDSIcon size={12} kind={MDSIconKind.CloudSlash} color={MDSIconColor.Secondary} />
              <span>Transcription disconnected, reconnecting...</span>
            </TranscriptStatus>
          )}
          {viewState.showCompromised && (
            <TranscriptStatus>
              <MDSIcon size={12} kind={MDSIconKind.Warning} color={MDSIconColor.Danger} />
              <span>
                Transcription disconnected in the middle of recording. We&apos;ll automatically
                regenerate it after you upload the recording.
              </span>
            </TranscriptStatus>
          )}
          {viewState.showFailureMessage && (
            <TranscriptStatus>
              <MDSIcon size={12} kind={MDSIconKind.CircleX} color={MDSIconColor.Danger} />
              <RedTextColor>{audioRecordingFailureMessage}</RedTextColor>
            </TranscriptStatus>
          )}
          <AudioCaptureIssues
            isAudioSilent={viewState.shouldShowAudioSilentWarning}
            isMicrophoneDisconnected={isMicrophoneDisconnected}
            isSpeakerAttributionHandoff={Boolean(transcript.handoffStatusLabel)}
          />
        </StyledTranscriptContent>
        {shouldShowDesktopDownloadNotice && (
          <AudioCaptureDownloadNotice onDownloadClick={onDownloadClick} />
        )}
      </StyledTranscriptContainer>
    );
  }

  if (viewState.mode === AudioCaptureTranscriptViewMode.StatusOnly) {
    return (
      <StyledTranscriptContainer>
        {viewState.showTranscriptionUnsupported && (
          <TranscriptStatus>
            <MDSIcon size={12} kind={MDSIconKind.TextSlash} color={MDSIconColor.Danger} />
            <RedTextColor>Transcription not supported on this application</RedTextColor>
          </TranscriptStatus>
        )}
        {viewState.showDisconnected && (
          <TranscriptStatus>
            <MDSIcon size={12} kind={MDSIconKind.CloudSlash} color={MDSIconColor.Secondary} />
            <span>Transcription disconnected, reconnecting...</span>
          </TranscriptStatus>
        )}
        {viewState.showCompromised && (
          <TranscriptStatus>
            <MDSIcon size={12} kind={MDSIconKind.Warning} color={MDSIconColor.Danger} />
            <span>
              Transcription disconnected in the middle of recording. We&apos;ll automatically
              regenerate it after you upload the recording.
            </span>
          </TranscriptStatus>
        )}
        {viewState.showFailureMessage && (
          <TranscriptStatus>
            <MDSIcon size={12} kind={MDSIconKind.CircleX} color={MDSIconColor.Danger} />
            <RedTextColor>{audioRecordingFailureMessage}</RedTextColor>
          </TranscriptStatus>
        )}

        {viewState.showListeningStatus && (
          <TranscriptStatus>
            <MDSIcon size={12} kind={MDSIconKind.Microphone} color={MDSIconColor.Secondary} />
            <span>
              {phase === AudioCapturePhase.Initializing ? "Starting Voice Mode..." : "Listening..."}
            </span>
          </TranscriptStatus>
        )}
        <AudioCaptureInitializationNotice
          shouldShow={showMicrophoneAccessNotice}
          onOpenMicrophoneSystemPermissions={onOpenMicrophoneSystemPermissions}
        />
        <AudioCaptureIssues
          isAudioSilent={viewState.shouldShowAudioSilentWarning}
          isMicrophoneDisconnected={isMicrophoneDisconnected}
          isSpeakerAttributionHandoff={Boolean(transcript.handoffStatusLabel)}
        />
        {shouldShowDesktopDownloadNotice && (
          <AudioCaptureDownloadNotice onDownloadClick={onDownloadClick} />
        )}
      </StyledTranscriptContainer>
    );
  }

  if (viewState.mode === AudioCaptureTranscriptViewMode.FailureOnly) {
    return (
      <StyledTranscriptContainer>
        <TranscriptStatus>
          <MDSIcon size={12} kind={MDSIconKind.CircleX} color={MDSIconColor.Danger} />
          <RedTextColor>{audioRecordingFailureMessage}</RedTextColor>
        </TranscriptStatus>
        <AudioCaptureIssues
          isAudioSilent={viewState.shouldShowAudioSilentWarning}
          isMicrophoneDisconnected={isMicrophoneDisconnected}
          isSpeakerAttributionHandoff={Boolean(transcript.handoffStatusLabel)}
        />
        {shouldShowDesktopDownloadNotice && (
          <AudioCaptureDownloadNotice onDownloadClick={onDownloadClick} />
        )}
      </StyledTranscriptContainer>
    );
  }

  return null;
}

export interface AudioCaptureTranscriptEntryGroup {
  id: string;
  alignment: MDSBubbleAlignment;
  entries: AudioCaptureTranscriptEntry[];
}

function AudioCaptureTranscriptBubbleGroup({ group }: { group: AudioCaptureTranscriptEntryGroup }) {
  const firstEntry = group.entries[0];
  const speakerDisplayName =
    firstEntry?.variant === MDSBubbleVariant.VoiceOthers
      ? firstEntry.speakerDisplayName
      : undefined;

  return (
    <TranscriptBubbleGroup $align={group.alignment}>
      {speakerDisplayName && (
        <SpeakerBadgeRow>
          <TranscriptSpeakerBadge name={speakerDisplayName} />
        </SpeakerBadgeRow>
      )}
      {group.entries.map((entry, index) => (
        <StyledBubble
          key={entry.id}
          align={entry.alignment}
          variant={entry.variant}
          isLastInGroup={index === group.entries.length - 1}
        >
          <TranscriptBubbleContent>
            <span>{entry.text}</span>
          </TranscriptBubbleContent>
        </StyledBubble>
      ))}
    </TranscriptBubbleGroup>
  );
}

export function groupAudioCaptureTranscriptEntries(
  entries: AudioCaptureTranscriptEntry[]
): AudioCaptureTranscriptEntryGroup[] {
  const groups: AudioCaptureTranscriptEntryGroup[] = [];

  for (const entry of entries) {
    const previousGroup = groups[groups.length - 1];
    const previousEntry = previousGroup?.entries[previousGroup.entries.length - 1];

    if (
      previousGroup &&
      previousEntry &&
      !previousEntry.isLastInGroup &&
      isSameTranscriptSpeaker(entry, previousEntry)
    ) {
      previousGroup.entries.push(entry);
    } else {
      groups.push({ id: entry.id, alignment: entry.alignment, entries: [entry] });
    }
  }

  return groups;
}

function isSameTranscriptSpeaker(
  entry: AudioCaptureTranscriptEntry,
  previousEntry: AudioCaptureTranscriptEntry
): boolean {
  if (hasSpeakerGroupKeys(entry, previousEntry)) {
    return previousEntry.speakerGroupKey === entry.speakerGroupKey;
  }

  return (
    previousEntry.alignment === entry.alignment &&
    previousEntry.variant === entry.variant &&
    previousEntry.speakerDisplayName === entry.speakerDisplayName
  );
}

/** Returns whether both adjacent bubbles can use stable speaker identity for grouping. */
function hasSpeakerGroupKeys(
  entry: AudioCaptureTranscriptEntry,
  previousEntry: AudioCaptureTranscriptEntry
): boolean {
  return previousEntry.speakerGroupKey != null && entry.speakerGroupKey != null;
}

interface AudioCaptureIssuesProps {
  isAudioSilent: boolean;
  isMicrophoneDisconnected: boolean;
  isSpeakerAttributionHandoff: boolean;
}

export function getMicrophoneDisconnectedStatusText({
  isSpeakerAttributionHandoff,
}: {
  isSpeakerAttributionHandoff: boolean;
}): string {
  if (isSpeakerAttributionHandoff) {
    return "Restarting Voice Mode to enable speaker attribution";
  }

  return "Microphone was disconnected, restarting voice mode...";
}

function AudioCaptureIssues({
  isAudioSilent,
  isMicrophoneDisconnected,
  isSpeakerAttributionHandoff,
}: AudioCaptureIssuesProps) {
  return (
    <>
      <AnimatePresence>
        {isAudioSilent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0, transition: { duration: 0.2, ease: "easeInOut" } }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <TranscriptStatus>
              <RedTextColor>No audio detected, please check your microphone.</RedTextColor>
            </TranscriptStatus>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isMicrophoneDisconnected && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{
              height: 0,
              opacity: 0,
              transition: { delay: 0.8, duration: 0.2, ease: "easeInOut" },
            }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <TranscriptStatus>
              <MDSIcon
                size={12}
                kind={MDSIconKind.MicrophoneSlash}
                color={MDSIconColor.Secondary}
              />
              <span>{getMicrophoneDisconnectedStatusText({ isSpeakerAttributionHandoff })}</span>
            </TranscriptStatus>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

interface AudioCaptureInitializationNoticeProps {
  shouldShow: boolean;
  onOpenMicrophoneSystemPermissions: () => void;
}

function AudioCaptureInitializationNotice({
  shouldShow,
  onOpenMicrophoneSystemPermissions,
}: AudioCaptureInitializationNoticeProps) {
  const isMacPlatform = isMac();
  const isWindowsPlatform = isWin();

  if (!isMacPlatform && !isWindowsPlatform) {
    return null;
  }

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0, transition: { duration: 0.2, ease: "easeInOut" } }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        >
          <TranscriptStatus>
            <MDSIcon size={12} kind={MDSIconKind.Warning} color={MDSIconColor.Danger} />
            {isMacPlatform ? (
              <span>
                We&apos;re having trouble accessing your microphone. Make sure that you&apos;ve
                granted Mem microphone access in{" "}
                <MDSLink onPress={onOpenMicrophoneSystemPermissions}>System Permissions</MDSLink>.
              </span>
            ) : (
              <span>
                We&apos;re having trouble accessing your microphone. Please try again later.
              </span>
            )}
          </TranscriptStatus>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface AudioCaptureDownloadNoticeProps {
  onDownloadClick: () => void;
}

function AudioCaptureDownloadNotice({ onDownloadClick }: AudioCaptureDownloadNoticeProps) {
  return (
    <DownloadDesktopAppNoticeOnWebContainer>
      <TranscriptStatus>
        <MDSIcon size={12} kind={MDSIconKind.Warning} color={MDSIconColor.Secondary} />
        <span>
          Web only transcribes what your microphone hears.{" "}
          <MDSLink onPress={onDownloadClick}>Download the Mem desktop app</MDSLink> to transcribe
          all system audio and capture complete meeting notes.
        </span>
      </TranscriptStatus>
    </DownloadDesktopAppNoticeOnWebContainer>
  );
}

const StyledTranscriptContainer = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing["4"]};
  overflow-y: auto;
  max-height: max(100px, 50vh);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  scrollbar-width: none;
  width: 100%;
`;

const StyledTranscriptContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["4"]};
  font-size: ${({ theme }) => theme.fontSizes["paragraph-s"]};
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: ${({ theme }) => theme.lineHeights["paragraph-sm"]};
  white-space: pre-wrap;
`;

const TranscriptStatus = styled.div`
  align-items: flex-start;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  flex-direction: row;
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  gap: ${({ theme }) => theme.spacing["1"]};
  padding-left: ${({ theme }) => theme.spacing["2"]};
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
  width: 100%;
`;

const RedTextColor = styled.span`
  color: ${({ theme }) => theme.colors.text.danger};
`;

const TranscriptBubbleGroup = styled.div<{ $align: MDSBubbleAlignment }>`
  align-items: ${({ $align }) =>
    $align === MDSBubbleAlignment.Left
      ? "flex-start"
      : $align === MDSBubbleAlignment.Center
        ? "center"
        : "flex-end"};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["0.5"]};
  width: 100%;
`;

const SpeakerBadgeRow = styled.div`
  max-width: 100%;
  width: fit-content;
`;

const StyledBubble = styled(MDSBubble)`
  width: 100%;

  & > * {
    max-width: ${({ theme }) => `calc(100% - ${theme.spacing[5]})`};

    ${({ align, theme }) =>
      align === MDSBubbleAlignment.Left
        ? `margin-right: ${theme.spacing[5]};`
        : `margin-left: ${theme.spacing[5]};`};
  }
`;

const TranscriptBubbleContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["1"]};
  max-width: 100%;
  min-width: 0;
`;

const DownloadDesktopAppNoticeOnWebContainer = styled.div`
  padding: ${({ theme }) => `${theme.spacing["2"]} ${theme.spacing["4"]} ${theme.spacing["2"]} 0`};
`;
